import { AddQueryFilterButton } from '@modules/queryBuilder/components/QueryBuilderFooter/AddQueryFilterButton';
import { useIsCreateNewFilterEnabled } from '@modules/queryBuilder/hooks';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { ErrorOutline, WarningAmberOutlined } from '@mui/icons-material';
import { Box, ButtonProps, Stack, Typography } from '@mui/material';
import { CreateNewFilterButton } from '../CreateNewFilterButton';

interface QueryBuilderFooterProps {
  shouldRenderCreateNewFilter?: boolean;
  shouldSkipWarningMessage?: boolean;
  buttonColor: ButtonProps['color'];
}

export function QueryBuilderFooter({
  shouldRenderCreateNewFilter = true,
  shouldSkipWarningMessage = false,
  buttonColor,
}: QueryBuilderFooterProps) {
  const shouldShowErrorMessage = useQueryBuilderStore(
    ({ metadata }) =>
      Boolean(metadata.error) ||
      (!metadata.isActive && metadata.hasIncompleteFilters),
  );
  const errorMessage = useQueryBuilderStore(
    ({ metadata }) => metadata.error?.message,
  );

  const isCreateNewFilterEnabled = useIsCreateNewFilterEnabled();
  const shouldShowWarningMessage = useQueryBuilderStore(
    ({ metadata }) =>
      !metadata.isActive &&
      metadata.hasUnsavedChanges &&
      !metadata.hasIncompleteFilters &&
      !metadata.error &&
      !shouldSkipWarningMessage,
  );
  const isMessageVisible = shouldShowErrorMessage || shouldShowWarningMessage;
  const shouldShowCreateNewFilterButton =
    isCreateNewFilterEnabled && shouldRenderCreateNewFilter;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: isMessageVisible ? 2 : undefined }}
    >
      <AddQueryFilterButton
        variant="text"
        fullWidth={!isMessageVisible && !shouldShowCreateNewFilterButton}
        color={buttonColor}
      />
      {shouldShowErrorMessage && (
        <Typography
          component={Stack}
          direction="row"
          alignItems="center"
          variant="body2"
          color="error.main"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          <ErrorOutline color="inherit" fontSize="inherit" />
          <Box component="span">
            {errorMessage ??
              'Filters feel incomplete. Please select at least one value to cheer them up!'}
          </Box>
        </Typography>
      )}
      {shouldShowWarningMessage && (
        <Typography
          component={Stack}
          direction="row"
          alignItems="center"
          variant="body2"
          color="warning.main"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          <WarningAmberOutlined color="inherit" fontSize="inherit" />
          <Box component="span">
            Query's feeling left out. Execute it to show some love!
          </Box>
        </Typography>
      )}
      {shouldShowCreateNewFilterButton && (
        <CreateNewFilterButton sx={{ ml: 2 }} color={buttonColor} />
      )}
    </Stack>
  );
}
